// SPDX-License-Identifier: AGPL-3.0-or-later

import {SettingsSection} from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/components/GuildOverviewTabSettingsSection';
import styles from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/GuildOverviewTab.module.css';
import type {ChannelLike} from '@app/features/guild/components/modals/guild_tabs/guild_overview_tab/GuildOverviewTypes';
import type {FormInputs, SelectOption} from '@app/features/guild/utils/guild_tabs/GuildOverviewTabUtils';
import {Select as FormSelect} from '@app/features/ui/components/form/FormSelect';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {msg} from '@lingui/core/macro';
import {Trans, useLingui} from '@lingui/react/macro';
import type React from 'react';
import {useMemo} from 'react';
import type {UseFormReturn} from 'react-hook-form';
import {Controller} from 'react-hook-form';

const NO_SYSTEM_CHANNEL_DESCRIPTOR = msg({
	message: 'No system channel',
	comment: 'Empty-state text in the system welcome section.',
});
const UNNAMED_CHANNEL_DESCRIPTOR = msg({
	message: 'Unnamed channel',
	comment: 'Short label in the system welcome section. Keep it concise.',
});
const DESTINATION_CHANNEL_DESCRIPTOR = msg({
	message: 'Destination channel',
	comment: 'Short label in the system welcome section. Keep it concise.',
});
const WELCOME_AND_SYSTEM_MESSAGES_WILL_APPEAR_HERE_DESCRIPTOR = msg({
	message: 'Welcome and system messages will appear here.',
	comment: 'Description text in the system welcome section.',
});
const SELECT_A_CHANNEL_DESCRIPTOR = msg({
	message: 'Select a channel',
	comment: 'Button or menu action label in the system welcome section. Keep it concise.',
});
const HIDE_JOIN_MESSAGES_DESCRIPTOR = msg({
	message: 'Hide join messages',
	comment: 'Short label in the system welcome section. Keep it concise.',
});
const WHEN_ENABLED_NEW_MEMBER_JOINS_WON_T_POST_DESCRIPTOR = msg({
	message: 'Suppresses join messages in the destination channel.',
	comment: 'Description text in the system welcome section.',
});
export const SystemWelcomeSection: React.FC<{
	form: UseFormReturn<FormInputs>;
	canManageGuild: boolean;
	textChannels: Array<ChannelLike>;
}> = ({form, canManageGuild, textChannels}) => {
	const {i18n} = useLingui();
	const systemChannelOptions = useMemo<Array<SelectOption>>(
		() => [
			{value: null, label: i18n._(NO_SYSTEM_CHANNEL_DESCRIPTOR)},
			...textChannels.map((channel) => ({
				value: channel.id,
				label: channel.name ?? i18n._(UNNAMED_CHANNEL_DESCRIPTOR),
			})),
		],
		[textChannels],
	);
	return (
		<SettingsSection
			title={<Trans>System & welcome</Trans>}
			description={<Trans>Choose destination for system and welcome messages</Trans>}
			data-flx="guild.guild-tabs.guild-overview-tab.system-welcome-section.settings-section"
		>
			<div
				className={styles.settingsContent}
				data-flx="guild.guild-tabs.guild-overview-tab.system-welcome-section.settings-content"
			>
				<div data-flx="guild.guild-tabs.guild-overview-tab.system-welcome-section.div">
					<Controller
						name="system_channel_id"
						control={form.control}
						render={({field}) => (
							<FormSelect<string | null>
								label={i18n._(DESTINATION_CHANNEL_DESCRIPTOR)}
								description={i18n._(WELCOME_AND_SYSTEM_MESSAGES_WILL_APPEAR_HERE_DESCRIPTOR)}
								value={field.value ?? null}
								onChange={(v) => field.onChange(v)}
								options={systemChannelOptions}
								placeholder={i18n._(SELECT_A_CHANNEL_DESCRIPTOR)}
								disabled={!canManageGuild}
								data-flx="guild.guild-tabs.guild-overview-tab.system-welcome-section.form-select.change"
							/>
						)}
						data-flx="guild.guild-tabs.guild-overview-tab.system-welcome-section.controller"
					/>
				</div>
			</div>
			<Controller
				name="suppress_join_notifications"
				control={form.control}
				render={({field}) => (
					<Switch
						label={i18n._(HIDE_JOIN_MESSAGES_DESCRIPTOR)}
						description={i18n._(WHEN_ENABLED_NEW_MEMBER_JOINS_WON_T_POST_DESCRIPTOR)}
						value={field.value ?? false}
						onChange={field.onChange}
						disabled={!canManageGuild}
						data-flx="guild.guild-tabs.guild-overview-tab.system-welcome-section.switch.change"
					/>
				)}
				data-flx="guild.guild-tabs.guild-overview-tab.system-welcome-section.controller--2"
			/>
		</SettingsSection>
	);
};
